import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { Lesson, Module } from 'core/models/course-content.model';
import { EvaluationResultsService } from 'core/services/evaluation-results.service';
import { CourseViewerService } from 'core/services/course-viewer.service';
import { LessonHeader } from 'components/lesson-header/lesson-header';

@Component({
  selector: 'app-evaluation-results-content',
  standalone: true,
  imports: [CommonModule, LessonHeader],
  templateUrl: './evaluation-results.html',
  styleUrl: './evaluation-results.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'w-full h-full flex flex-col',
  },
})
export class EvaluationResults implements OnInit {
  private readonly evaluationResultsService = inject(EvaluationResultsService);
  private readonly courseService = inject(CourseViewerService);

  @Input({ required: true }) lesson!: Lesson;
  @Input() module?: Module | null;

  protected results = signal(this.evaluationResultsService.getResults());

  ngOnInit(): void {
    // Atualizar resultados quando o serviço mudar
    this.evaluationResultsService.results$.subscribe((results) => {
      this.results.set(results);
    });
  }

  /**
   * Obtém a mensagem de feedback baseada na porcentagem
   */
  protected get feedbackMessage(): string {
    const results = this.results();
    if (!results) {
      return '';
    }

    const percentage = results.percentage;

    if (percentage === 100) {
      return 'Parabéns! Você acertou todas as questões!';
    } else if (percentage >= 80) {
      return 'Excelente! Você teve um ótimo desempenho!';
    } else if (percentage >= 60) {
      return 'Bom trabalho! Continue estudando para melhorar ainda mais.';
    } else if (percentage >= 40) {
      return 'Você está no caminho certo. Revise o conteúdo e tente novamente.';
    } else {
      return 'Não desista! Revise o material e tente novamente.';
    }
  }

  /**
   * Obtém a questão pelo ID
   */
  private getQuestionById(questionId: string) {
    const course = this.courseService.getCourse();
    const module5 = course.modules.find((m) => m.id === 'module-5');

    if (
      !module5 ||
      module5.contentType !== 'evaluations' ||
      !module5.questions
    ) {
      return null;
    }

    return module5.questions.find((q) => q.id === questionId) || null;
  }

  /**
   * Obtém o texto da questão pelo ID com índice
   */
  protected getQuestionText(questionId: string, lessonId: string): string {
    const question = this.getQuestionById(questionId);
    if (!question) {
      return 'Questão não encontrada';
    }

    // Retornar texto com índice (order + 1)
    return `${question.order + 1}. ${question.text}`;
  }

  /**
   * Obtém o texto da opção pelo ID
   */
  protected getOptionText(
    questionId: string,
    optionId: string,
    lessonId: string,
  ): string {
    const question = this.getQuestionById(questionId);
    if (!question) {
      return '';
    }

    const option = question.options.find((o) => o.id === optionId);
    return option ? option.text : '';
  }

  /**
   * Verifica se uma opção é a resposta correta
   */
  protected isCorrectAnswer(
    questionId: string,
    optionId: string,
    lessonId: string,
  ): boolean {
    const question = this.getQuestionById(questionId);
    if (!question) {
      return false;
    }

    return question.correctAnswers.includes(optionId);
  }

  /**
   * Obtém todas as questões do módulo 5 ordenadas por order
   */
  protected getAllQuestions(): Array<{
    questionId: string;
    text: string;
    lessonId: string;
    order: number;
  }> {
    const course = this.courseService.getCourse();
    const module5 = course.modules.find((m) => m.id === 'module-5');

    if (
      !module5 ||
      module5.contentType !== 'evaluations' ||
      !module5.questions
    ) {
      return [];
    }

    // Ordenar questões pelo campo order
    const sortedQuestions = [...module5.questions].sort(
      (a, b) => a.order - b.order,
    );

    return sortedQuestions.map((question) => ({
      questionId: question.id,
      text: question.text,
      lessonId: this.lesson.id,
      order: question.order,
    }));
  }

  /**
   * Obtém os resultados ordenados por ordem das questões
   * Garante que todas as questões do módulo apareçam, mesmo que não tenham sido visitadas
   */
  protected getOrderedQuestionResults() {
    const results = this.results();
    const allQuestions = this.getAllQuestions();

    if (allQuestions.length === 0) {
      return [];
    }

    // Criar um mapa de resultados existentes por questionId
    const resultsMap = new Map<string, any>();
    if (results && results.questionResults) {
      results.questionResults.forEach((r) => {
        resultsMap.set(r.questionId, r);
      });
    }

    // Buscar rascunhos/respostas salvas para questões que não têm resultado formal
    const course = this.courseService.getCourse();
    const module5 = course.modules.find((m) => m.id === 'module-5');

    if (
      !module5 ||
      module5.contentType !== 'evaluations' ||
      !module5.questions
    ) {
      return [];
    }

    // Variável local para garantir que module5.questions não seja undefined
    const module5Questions = module5.questions;

    // Criar array de resultados completos para todas as questões
    const completeResults: any[] = [];

    allQuestions.forEach((question) => {
      let questionResult = resultsMap.get(question.questionId);
      const questionData = module5Questions.find(
        (q) => q.id === question.questionId,
      );

      // Se não há resultado formal, buscar respostas salvas (rascunhos)
      if (!questionResult && questionData) {
        // Buscar rascunhos de todas as lições do módulo 5
        // Cada questão tem uma lição correspondente: lesson-module-5-{questionId}
        const lessonIdForQuestion = `lesson-module-5-${question.questionId}`;
        let draftAnswers = this.evaluationResultsService.getQuestionDraft(
          lessonIdForQuestion,
          question.questionId,
        );

        // Se não encontrou na lição específica, buscar em todas as lições do módulo
        // Isso garante que respostas salvas sejam encontradas mesmo se o aluno não navegou até a questão
        if (draftAnswers.length === 0) {
          // Buscar em todas as lições convertidas do módulo 5
          const convertedLessons =
            this.courseService.convertQuestionsToLessons(module5);
          for (const lesson of convertedLessons) {
            if (lesson.evaluationData && lesson.evaluationData.questions) {
              const hasThisQuestion = lesson.evaluationData.questions.some(
                (q) => q.id === question.questionId,
              );
              if (hasThisQuestion) {
                const draft = this.evaluationResultsService.getQuestionDraft(
                  lesson.id,
                  question.questionId,
                );
                if (draft.length > 0) {
                  draftAnswers = draft;
                  break;
                }
              }
            }
          }
        }

        if (draftAnswers.length > 0) {
          // Criar resultado baseado no rascunho encontrado
          questionResult = this.createResultFromDraft(
            question.questionId,
            lessonIdForQuestion,
            draftAnswers,
            questionData,
          );
        }
      }

      // Se ainda não há resultado, criar um resultado vazio para exibir a questão
      if (!questionResult) {
        questionResult = {
          questionId: question.questionId,
          lessonId: this.lesson.id,
          correct: false,
          points: 0,
          selectedAnswers: [],
          correctCount: 0,
          incorrectCount: 0,
          totalCorrect: questionData?.correctAnswers?.length || 0,
        };
      }

      completeResults.push(questionResult);
    });

    // Ordenar pelo order da questão
    return completeResults.sort((a, b) => {
      const questionA = allQuestions.find((q) => q.questionId === a.questionId);
      const questionB = allQuestions.find((q) => q.questionId === b.questionId);
      const orderA = questionA?.order ?? 999;
      const orderB = questionB?.order ?? 999;
      return orderA - orderB;
    });
  }

  /**
   * Cria um resultado de questão baseado em rascunho/resposta salva
   */
  private createResultFromDraft(
    questionId: string,
    lessonId: string,
    selectedAnswers: string[],
    questionData: any,
  ): any {
    const correctAnswers = questionData.correctAnswers || [];

    // Calcular contagem de respostas corretas e incorretas selecionadas
    const correctCount = selectedAnswers.filter((id: string) =>
      correctAnswers.includes(id),
    ).length;
    const incorrectCount = selectedAnswers.filter(
      (id: string) => !correctAnswers.includes(id),
    ).length;
    const totalCorrect = correctAnswers.length;

    // Verificar se está correto
    let isCorrect = false;
    if (
      questionData.type === 'single-choice' ||
      questionData.type === 'boolean'
    ) {
      isCorrect =
        selectedAnswers.length === 1 &&
        correctAnswers.length === 1 &&
        selectedAnswers[0] === correctAnswers[0];
    } else if (questionData.type === 'multiple-choice') {
      isCorrect =
        selectedAnswers.length === correctAnswers.length &&
        selectedAnswers.length > 0 &&
        selectedAnswers.every((id: string) => correctAnswers.includes(id)) &&
        correctAnswers.every((id: string) => selectedAnswers.includes(id));
    }

    const points = isCorrect ? questionData.points : 0;

    return {
      questionId,
      lessonId,
      correct: isCorrect,
      points,
      selectedAnswers: [...selectedAnswers],
      correctCount,
      incorrectCount,
      totalCorrect,
    };
  }

  /**
   * Obtém as respostas selecionadas na ordem original (mais antiga para mais atual)
   */
  protected getOrderedSelectedAnswers(selectedAnswers: string[]): string[] {
    return [...selectedAnswers];
  }

  /**
   * Verifica se a questão é de múltipla escolha e se deve exibir o aviso sobre gabarito completo
   * Mostra o aviso em todas as questões de múltipla escolha quando o resultado for incorreto
   */
  protected shouldShowMultipleChoiceWarning(questionResult: any): boolean {
    const question = this.getQuestionById(questionResult.questionId);
    if (!question || question.type !== 'multiple-choice') {
      return false;
    }

    // Mostrar aviso em todas as questões de múltipla escolha quando incorretas
    return !questionResult.correct;
  }
}
